import React, {
  useState, useEffect, useContext, useCallback, useMemo, useRef
} from 'react';
import classNames from 'classnames/bind.js';
import PropTypes from 'prop-types';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragOverlay,
  MeasuringStrategy,
  pointerWithin,
  rectIntersection,
  getFirstCollision
} from '@dnd-kit/core';
import {
  restrictToWindowEdges, restrictToVerticalAxis
} from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import {
  findItem,
  generateArrangedGroup
} from './dragAndDropHelper';
import styles from './drag-and-drop.module.scss';

const cx = classNames.bind(styles);
const DragAndDrop = ({
  id,
  items,
  groups,
  renderDraggedItem,
  onRearrangeUpdate,
  setGroups,
  children,
  userSelect,
}) => {
  const debounceDragTime = useRef(null);
  const [arrangingItems, setArrangingItems] = useState(items);
  const [activeItemObj, setActiveItemObj] = useState({
    activeId: null,
    activeGroup: null,
    activeItem: null,
    startIndex: -1
  });
  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';
  const lastOverId = useRef(null);
  const recentlyMovedToNewContainer = useRef(false);

  useEffect(() => {
    setArrangingItems(items);
  }, [items]);

  useEffect(() => { LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('drag-and-drop.ready'); }, []);

  useEffect(() => {
    requestAnimationFrame(() => {
      recentlyMovedToNewContainer.current = false;
    });
  }, [groups]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
    useSensor(TouchSensor)
  );

  const isTooQuick = () => {
    const currentTime = Date.now();
    if (debounceDragTime.current) {
      // If the drag is too fast, skip the collision detection
      return (currentTime - debounceDragTime.current < 200);
    }
    return false;
  }

  const defaultMatch = () => {
    return lastOverId.current ? [{ id: lastOverId.current }] : [];
  }

  const collisionDetectionStrategy = useCallback((args) => {
    const currentTime = Date.now();
    if (isTooQuick()) {
      return defaultMatch();
    }
    debounceDragTime.current = currentTime;
    // find any intersecting droppable
    const pointerIntersections = pointerWithin(args);
    // If there are droppables intersecting with the pointer, return those
    const intersections = pointerIntersections.length > 0 ? pointerIntersections : rectIntersection(args);
    let overId = getFirstCollision(intersections, 'id');
    if (overId != null) {
      if (overId in groups) {
        const groupItems = groups[overId];
        const isClosedContainer = args.droppableContainers.find(
          (container) => container.id === overId
        )?.data?.current?.isClosed;

        // Only redirect to specific items if the group has items AND is not a closed container
        if (groupItems.length > 0 && !isClosedContainer) {
          overId = closestCenter({
            ...args,
            droppableContainers: args.droppableContainers.filter(
              (container) => (
                container.id !== overId && groupItems.includes(container.id)
              )
            ),
          })[0]?.id;
        }
        // For empty groups or closed containers, keep the original overId (the group container itself)
      }
      lastOverId.current = overId;

      return [{ id: overId }];
    }
    // keep the previous activeId if the item was recently moved to a new container
    if (recentlyMovedToNewContainer.current) {
      lastOverId.current = activeItemObj.activeId;
    }
    // If no droppable is matched, return the last match
    return defaultMatch();
  }, [activeItemObj.activeId, groups]);

  const handleDragStart = (event) => {
    const { active } = event;
    debounceDragTime.current = null;
    let activeItem = null;
    let activeGroup = null;
    let startIndex = -1;
    if (!groups) {
      activeItem = arrangingItems.find((item) => findItem(item, active));
    } else {
      activeGroup = active.data?.current?.groupId;
      activeItem = active.data?.current?.item;
      startIndex = groups[activeGroup].indexOf(active?.id);
    }
    setActiveItemObj({
      activeId: active?.id,
      activeGroup,
      activeItem,
      startIndex
    });
  };

  const updateGroup = useCallback(({
    activeContainer,
    overContainer,
    over,
    active
                                   }) => {
    const overItem = over?.id;
    const activeItem = active?.id;
    setGroups((previousGroupItems) => {
      const activeItems = previousGroupItems[activeContainer];
      const overItems = previousGroupItems[overContainer];
      const overIndex = overItems.indexOf(overItem);
      const activeIndex = activeItems.indexOf(activeItem);

      let newIndex;

      if (overItem in previousGroupItems) {
        newIndex = overItems.length + 1;
      } else {
        const isBelowOverItem = over
          && active.rect.current.translated
          && active.rect.current.translated.top
          > over.rect.top + over.rect.height;

        const modifier = isBelowOverItem ? 1 : 0;

        newIndex = overIndex >= 0 ? overIndex + modifier : overItems.length + 1;
      }

      recentlyMovedToNewContainer.current = true;

      return {
        ...previousGroupItems,
        [activeContainer]: previousGroupItems[activeContainer].filter(
          (item) => item !== activeItem
        ),
        [overContainer]: [
          ...previousGroupItems[overContainer].slice(0, newIndex),
          previousGroupItems[activeContainer][activeIndex],
          ...previousGroupItems[overContainer].slice(
            newIndex,
            previousGroupItems[overContainer].length
          ),
        ],
      };
    });
  }, [setGroups]);

  const handleDragOver = ({ active, over }) => {
    const overData = over?.data?.current;
    const activeData = active?.data?.current;
    const overContainer = overData?.groupId;
    const activeContainer = activeData?.groupId;
    if (!overContainer || !activeContainer) {
      return;
    }
    if (activeContainer !== overContainer && !Boolean(overData?.isClosed)) {
      updateGroup({ activeContainer, overContainer, over, active });
    }
  };

  const isOverAnotherElement = ({ over, active, prevGroupId, activeGroup}) => {
    return !!(over?.id
      && (
        active?.id !== over?.id
        || (
          groups && (prevGroupId !== activeGroup)
        )
      ));
  };

  const dragEndNonGroup = ({ active, over }) => {
    let newItemOrder = [];
    let prevIndex;
    let newIndex = 0;
    setArrangingItems((previousItems) => {
      prevIndex = previousItems.findIndex((item) => findItem(item, active));
      newIndex = previousItems.findIndex((item) => findItem(item, over));
      newItemOrder = arrayMove(previousItems, prevIndex, newIndex);
      return newItemOrder;
    });
    onRearrangeUpdate({
      items: newItemOrder,
      item: active?.id,
      prevIndex,
      newIndex
    });
  };

  const dragEndGroup = ({ active, over }) => {
    let groupedItems = [];
    let newIndex = 0;
    const prevGroupId = activeItemObj.activeGroup;
    let prevIndex = groups[prevGroupId].indexOf(active.id);
    const activeGroup = active?.data?.current?.groupId;
    let overGroup = over?.data?.current?.groupId;
    const isClosedGroup = Boolean(over?.data?.current?.isClosed);
    if (!prevGroupId) {
      return false;
    }

    const overId = over?.id;

    if (!Boolean(overId)) {
      return false;
    }
    if (overGroup) {
      if (isClosedGroup) {
        newIndex = 0;
        onRearrangeUpdate({
          groupedItems: generateArrangedGroup(groupedItems),
          item: activeItemObj.activeItem,
          prevGroupId,
          newGroupId: overGroup,
          prevIndex: activeItemObj.startIndex,
          newIndex
        });
      } else {
        const activeIndex = groups[activeGroup]?.indexOf(active.id);
        newIndex = groups[overGroup]?.indexOf(overId);
        if (newIndex === -1) {
          overGroup = activeGroup;
          newIndex = activeIndex;
          groupedItems = groups;
        } else if (activeGroup !== overGroup) {
          setGroups((group) => {
            groupedItems = {
              ...group,
              [activeGroup]: group[activeGroup].filter(
                (item) => item !== active.id
              ),
              [overGroup]: [
                ...group[overGroup].slice(0, newIndex),
                group[activeGroup][activeIndex],
                ...group[overGroup].slice(newIndex, group[overGroup].length)
              ],
            };
            return groupedItems;
          });
        } else if (activeIndex !== newIndex) {
          setGroups((group) => {
            groupedItems = {
              ...group,
              [overGroup]: arrayMove(
                group[overGroup],
                activeIndex,
                newIndex
              ),
            };
            return groupedItems;
          });
        } else if (activeIndex === newIndex) {
          groupedItems = groups;
        }
        onRearrangeUpdate({
          groupedItems: generateArrangedGroup(groupedItems),
          item: activeItemObj.activeItem,
          prevGroupId,
          newGroupId: overGroup,
          prevIndex: activeItemObj.startIndex,
          newIndex
        });
      }
    }
    return true;
  }

  const handleDragEnd = (event) => {
    const { active, over } = event;
    debounceDragTime.current = null;
    const prevGroupId = activeItemObj.activeGroup;
    const activeGroup = active?.data?.current?.groupId;
    if (isOverAnotherElement({ over, active, prevGroupId, activeGroup })) {
      if (!groups) {
        dragEndNonGroup({ active, over });
      } else {
        dragEndGroup({ active, over });
      }
      setActiveItemObj({
        activeId: null,
        activeItem: null,
        activeGroup: null,
        startIndex: -1
      });
    }
  };

  const modifiers = isMobile ? [restrictToVerticalAxis] : [restrictToWindowEdges];
  const draggingClass = cx(
    styles.Sortable, styles.dragOverlay
  );

  return (
    <div data-component="DragAndDrop">
      <DndContext
        id={id}
        sensors={sensors}
        modifiers={modifiers}
        collisionDetection={groups ? collisionDetectionStrategy : closestCenter}
        measuring={{
          draggable: {
            strategy: MeasuringStrategy.WhileDragging
          },
        }}
        onDragOver={handleDragOver}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        {
          groups ? (children) : (
            <SortableContext
              items={arrangingItems}
              strategy={verticalListSortingStrategy}
            >
              {
                children
              }
            </SortableContext>
          )
        }
        <DragOverlay>
          {
            (!!activeItemObj.activeId) && (
              <div className={draggingClass}>
                <div className={cx(styles.Item, styles.dragOverlay)}>
                  {
                    renderDraggedItem({
                      id: activeItemObj.activeId,
                      item: activeItemObj.activeItem,
                      groupId: activeItemObj.activeGroup
                    })
                  }
                </div>
              </div>
            )
          }
        </DragOverlay>
      </DndContext>
    </div>
  );
};

DragAndDrop.propTypes = {
  id: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(PropTypes.string),
  groups: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
  onRearrangeUpdate: PropTypes.func.isRequired,
  renderDraggedItem: PropTypes.func.isRequired,
  setGroups: PropTypes.func,
  children: PropTypes.node,
  userSelect: PropTypes.string
};

DragAndDrop.defaultProps = {
  children: null,
  items: null,
  groups: null,
  setGroups: () => {},
  userSelect: 'none'
};

DragAndDrop.displayName = 'DragAndDrop';

export { DragAndDrop };
